/** Colours of the three.js view cube for one theme. The highlight colours are the editor's accent tokens. */
export interface CubePalette {
  face: string;
  edge: string;
  label: string;
  highlightFill: string;
  highlightLine: string;
  cornerMark: string;
  cornerMarkOpacity: number;
  roughness: number;
  metalness: number;
  /** Ambient light on the cube; light faces need more so every side stays light enough behind its label. */
  ambient: number;
}

const DARK: CubePalette = {
  face: '#262626',
  edge: '#4d4d4d',
  label: '#f2f2f2',
  highlightFill: '#2f3f16',
  highlightLine: '#a0ff00',
  cornerMark: '#ffffff',
  cornerMarkOpacity: 0.08,
  roughness: 0.35,
  metalness: 0.15,
  ambient: 0.35,
};

const LIGHT: CubePalette = {
  face: '#ffffff',
  edge: '#c9c9c9',
  label: '#1a1a1a',
  highlightFill: '#e8ffc2',
  highlightLine: '#335300',
  cornerMark: '#000000',
  cornerMarkOpacity: 0.06,
  roughness: 0.05,
  metalness: 0,
  ambient: 1.6,
};

export function cubePalette(isDark: boolean): CubePalette {
  return isDark ? DARK : LIGHT;
}

function luminance(hex: string): number {
  const channel = (i: number): number => {
    const c = parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}

/** Contrast ratio between two `#rrggbb` colours. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}
